import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HeroSection = ({ data, onDonateClick, onVolunteerClick }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    zip: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);

  const logo = data?.candidate?.logo || '';
  const tagline = data?.candidate?.tagline || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (smsConsent && formData.phone.replace(/[^0-9]/g, '').length < 10) {
      toast({
        title: "Mobile number required",
        description: "To receive text messages, please enter a valid mobile phone number.",
        variant: "destructive"
      });
      return;
    }
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/volunteer`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        zip: formData.zip,
        sms_consent: smsConsent
      });
      
      toast({
        title: "Thank you for joining!",
        description: response.data.message || "We'll be in touch soon."
      });
      
      setFormData({ name: '', email: '', phone: '', zip: '' });
      setSmsConsent(false);
    } catch (error) {
      console.error('Volunteer submission error:', error);
      toast({
        title: "Submission failed",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-purple-950 overflow-hidden" data-testid="hero-section">
      {/* LEFT: Purple panel with headline + form */}
      <div className="relative z-10 flex items-center bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 order-2 lg:order-1">
        <div className="w-full max-w-xl mx-auto px-6 sm:px-10 py-16 lg:py-24 pt-12 lg:pt-28">
          <div className="space-y-8">
            {/* Headline */}
            <div className="space-y-4">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white leading-[0.95] tracking-tight" data-testid="hero-headline">
                Join Our<br />Campaign
              </h1>
              <div className="border-l-4 border-purple-400 pl-4">
                <h2 className="text-2xl sm:text-3xl font-bold italic text-white leading-tight">
                  &ldquo;Literacy is Liberation&rdquo;
                </h2>
                <p className="text-lg text-purple-100 font-medium mt-1">
                  For education, safety, and affordability
                </p>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="hero-volunteer-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Input
                  type="text"
                  placeholder="Name*"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  disabled={isSubmitting}
                  data-testid="hero-input-name"
                  className="h-14 bg-white/15 backdrop-blur-md text-white placeholder:text-white/70 text-base border border-white/30 rounded-xl shadow-lg focus:bg-white/20 focus:border-white/60 transition-all"
                />
                <Input
                  type="email"
                  placeholder="Email*"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={isSubmitting}
                  data-testid="hero-input-email"
                  className="h-14 bg-white/15 backdrop-blur-md text-white placeholder:text-white/70 text-base border border-white/30 rounded-xl shadow-lg focus:bg-white/20 focus:border-white/60 transition-all"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input
                  type="tel"
                  placeholder={smsConsent ? "Mobile phone*" : "Phone"}
                  aria-label="Mobile phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required={smsConsent}
                  disabled={isSubmitting}
                  data-testid="hero-input-phone"
                  className="h-14 bg-white/15 backdrop-blur-md text-white placeholder:text-white/70 text-base border border-white/30 rounded-xl shadow-lg focus:bg-white/20 focus:border-white/60 transition-all"
                />
                <Input
                  type="text"
                  placeholder="Zip Code*"
                  pattern="[0-9]{5}"
                  value={formData.zip}
                  onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                  required
                  disabled={isSubmitting}
                  data-testid="hero-input-zip"
                  className="h-14 bg-white/15 backdrop-blur-md text-white placeholder:text-white/70 text-base border border-white/30 rounded-xl shadow-lg focus:bg-white/20 focus:border-white/60 transition-all"
                />
              </div>
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="hero-sms-consent"
                  checked={smsConsent}
                  onChange={(e) => setSmsConsent(e.target.checked)}
                  disabled={isSubmitting}
                  className="mt-1 h-5 w-5 flex-shrink-0 cursor-pointer rounded accent-white"
                  data-testid="hero-sms-consent-checkbox"
                />
                <label htmlFor="hero-sms-consent" className="text-sm text-white/90 leading-relaxed cursor-pointer">
                  I agree to receive recurring SMS/MMS text messages from Friends of Clianda Florence.
                </label>
              </div>
              <p className="text-xs text-white/70 leading-relaxed" data-testid="hero-sms-disclosure">
                Optional. Message frequency varies. Msg &amp; data rates may apply. Reply HELP for help or STOP to opt out.
                Consent is not a condition of donating, volunteering, or supporting the campaign. See our{' '}
                <Link to="/terms-and-conditions/" className="underline font-semibold text-white">Terms &amp; Conditions</Link> and{' '}
                <Link to="/privacy-policy/" className="underline font-semibold text-white">Privacy Policy</Link>.
                Paid for by Friends of Clianda Florence.
              </p>
              <Button
                type="submit"
                disabled={isSubmitting}
                data-testid="hero-join-button"
                className="w-full h-14 bg-white text-purple-900 hover:bg-purple-100 font-bold uppercase text-lg rounded-xl disabled:opacity-50 disabled:cursor-not-allowed shadow-2xl transition-all hover:scale-[1.02]"
              >
                {isSubmitting ? 'Joining...' : 'Join Us'}
              </Button>
            </form>

            {/* Disclaimer */}
            <p className="text-xs text-white/70 leading-relaxed">
              Submissions sent to cliandaflorence@friendsofcliandaflorence.com
            </p>
          </div>
        </div>
      </div>

      {/* RIGHT: Her photo - face fully visible, never covered by text */}
      <div className="relative order-1 lg:order-2 h-[60vh] sm:h-[70vh] lg:h-auto lg:min-h-screen bg-purple-900" data-testid="hero-photo">
        <img
          src="https://customer-assets.emergentagent.com/job_florence-assembly/artifacts/qk4thg11_IMG_6546.jpeg"
          alt="Clianda Florence"
          className="w-full h-full object-cover"
          style={{ objectPosition: 'center 25%' }}
        />
        {/* Light purple tint only on the seam edge for a cohesive blend */}
        <div className="hidden lg:block absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-purple-900/60 to-transparent"></div>
        <div className="lg:hidden absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-purple-900/70 to-transparent"></div>
      </div>

      {/* Scroll Indicator */}
      <button
        onClick={() => {
          document.getElementById('about').scrollIntoView({ behavior: 'smooth' });
        }}
        className="hidden lg:block absolute bottom-8 left-1/4 -translate-x-1/2 z-20 text-white/80 hover:text-white transition-all animate-bounce"
        aria-label="Scroll down"
        data-testid="hero-scroll-down"
      >
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </button>
    </section>
  );
};

export { HeroSection };
